import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

interface Hit { key: string; title: string; sub: string; href: string }

/**
 * Поиск ⌘K по клиентам/контрагентам и заказам: два запроса (counterparties.list q,
 * orders.list q), результаты — deep-link на карточку. Debounce 200 мс.
 */
export function SearchOverlay({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const hitsRef = useRef<Hit[]>([]); hitsRef.current = hits;
  const activeRef = useRef(0); activeRef.current = active;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      const list = hitsRef.current;
      if (event.key === 'ArrowDown' && list.length) { event.preventDefault(); setActive((i) => (i + 1) % list.length); }
      else if (event.key === 'ArrowUp' && list.length) { event.preventDefault(); setActive((i) => (i - 1 + list.length) % list.length); }
      else if (event.key === 'Enter' && list.length) { event.preventDefault(); const h = list[Math.min(activeRef.current, list.length - 1)]; if (h) { onClose(); navigate(h.href); } }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, navigate]);

  useEffect(() => setActive(0), [hits]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setHits([]); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      Promise.all([
        api.query<{ items: Array<{ id: number; name: string; kinds: string[]; public_id: string }> }>('counterparties.list', { q, include_archived: true }).catch(() => ({ data: { items: [] } })),
        api.query<{ items: Array<{ id: number; public_id: string; client_name: string; status: string }> }>('orders.list', { q, limit: 20 }).catch(() => ({ data: { items: [] } })),
      ]).then(([cps, orders]) => {
        if (!alive) return;
        setHits([
          ...cps.data.items.slice(0, 8).map((c) => ({ key: `cp${c.id}`, title: c.name || '(без названия)', sub: `Контрагент · ${c.public_id}`, href: `/counterparties?kind=all&sel=${c.id}` })),
          ...orders.data.items.map((o) => ({ key: `o${o.id}`, title: o.client_name, sub: `Заказ ${o.public_id}`, href: `/orders/${o.id}` })),
        ]);
        setLoading(false);
      });
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);

  return (
    <div className="search-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="search-overlay__panel" role="dialog" aria-label="Поиск">
        <input className="search-overlay__input" autoFocus placeholder="Клиент, контрагент или номер заказа…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Поиск" />
        <div>
          {loading && <div className="search-overlay__note">Ищу…</div>}
          {!loading && query.trim().length >= 2 && hits.length === 0 && <div className="search-overlay__note">Ничего не найдено</div>}
          {hits.map((h, i) => (
            <button key={h.key} type="button" className={`search-overlay__row${i === active ? ' is-active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => { onClose(); navigate(h.href); }}>
              <strong>{h.title}</strong> <span className="t-secondary">{h.sub}</span>
            </button>
          ))}
        </div>
        <div className="search-overlay__foot"><span className="search-overlay__esc">Esc — закрыть · ↑↓ — выбор · Enter — открыть</span></div>
      </div>
    </div>
  );
}
